"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { ChevronRight, Play, CheckCircle2, Search, ShieldCheck, Star, MapPin } from "lucide-react";
import { useState } from "react";
import { ALL_SERVICES, type ServiceItem } from "@/lib/services";
import { useRouter } from "next/navigation";

export function EnterpriseHero() {
  const [jobQuery, setJobQuery] = useState("");
  const router = useRouter();

  const [showDropdown, setShowDropdown] = useState(false);

  /// Query ko words me todo (2+ letter wale). Har service ko score do —
  // jitne zyada words match karein, utna upar. Isse "get garden" me bhi
  // "garden" match ho jaata hai (kyunki "get" kahin nahi hai to skip).
  const tokens = jobQuery.trim().toLowerCase().split(/\s+/).filter((t) => t.length >= 2);
  const matches = tokens.length
    ? ALL_SERVICES
        .map((s) => {
          const text = `${s.label} ${s.description} ${s.id}`.toLowerCase();
          const score = tokens.reduce((acc, t) => acc + (text.includes(t) ? 1 : 0), 0);
          return { service: s, score };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8)
        .map((x) => x.service)
    : [];

  // Dropdown me kisi service pe click → booking page khulega
  const selectService = (s: ServiceItem) => {
    setShowDropdown(false);
    router.push(`/booking?category=${encodeURIComponent(s.id)}`);
  };

  const startBooking = () => {
    const query = jobQuery.trim();
    router.push(query ? `/booking?description=${encodeURIComponent(query)}` : "/booking");
  };

  return (
    <section className="relative pt-28 pb-16 md:pt-40 md:pb-24 overflow-x-clip bg-white">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-black uppercase tracking-wider mb-6"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Trusted local pros, transparent fixes
            </motion.div>
            
            <h1 className="text-[2.5rem] font-black tracking-tight text-slate-950 leading-[1.08] mb-6">
              Book trusted home services with confidence.
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
              Tell us what needs fixing, get a clear upfront estimate, and connect with vetted local professionals for residential and commercial work.
            </p>

            <div className="relative w-full md:w-[80%] mb-6">
  <div className="rounded-3xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-200/70">
    <div className="flex flex-col sm:flex-row gap-2">
      <label className="flex flex-1 items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 border border-slate-100">
        <Search size={18} className="text-slate-400" />
        <input
          value={jobQuery}
          onChange={(event) => { setJobQuery(event.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 120)}
          onKeyDown={(event) => {
            if (event.key === "Enter") startBooking();
          }}
          placeholder="What do you need help with?"
          className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none"
        />
      </label>
      <button
        onClick={startBooking}
        className="px-6 py-3 rounded-2xl bg-emerald-600 text-white text-sm font-black hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 flex items-center justify-center gap-2 active:scale-[0.98]"
      >
        Get estimate
        <ChevronRight size={16} />
      </button>
    </div>
  </div>

  {/* 🔽 Dropdown */}
  {showDropdown && jobQuery.trim() && (
    <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-2xl border border-slate-200 bg-white shadow-2xl max-h-80 overflow-y-auto">
      {matches.length > 0 ? (
        matches.map((s) => (
          <button
            key={s.id}
            onMouseDown={(e) => { e.preventDefault(); selectService(s); }}
            className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0 transition-colors"
          >
            <Search size={16} className="text-slate-400 shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-900 truncate">{s.label}</span>
              <span className="block text-xs text-slate-400 truncate">{s.description}</span>
            </span>
          </button>
        ))
      ) : (
        <div className="px-4 py-4 text-sm text-slate-400 text-center">No matching services for "{jobQuery}"</div>
      )}
    </div>
  )}
</div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                href="/booking"
                className="w-full sm:w-auto px-4 py-3 rounded-2xl bg-slate-950 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-2 group active:scale-[0.98]"
              >
                Browse services
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/#how-it-works"
                className="w-full sm:w-auto px-4 py-3 rounded-2xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <Play size={16} fill="currentColor" />
                How it works
              </Link>
            </div>
            
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: "Vetted pros", icon: <ShieldCheck size={17} className="text-emerald-600" /> },
                { label: "Upfront pricing", icon: <CheckCircle2 size={17} className="text-emerald-600" /> },
                { label: "Local coverage", icon: <MapPin size={17} className="text-emerald-600" /> },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700">
                  {item.icon}
                  {item.label}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl border border-slate-200 bg-slate-950">
              <img
                src="/hero-tradesperson.jpg"
                alt="FIXER service dashboard preview"
                className="w-full h-auto object-cover opacity-95"
              />
            </div>

            {/* Floating Info Card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -right-6 bg-white p-5 rounded-2xl shadow-xl border border-slate-100 z-20 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 bg-emerald-600 rounded-xl flex items-center justify-center text-white">
                  <Star size={20} fill="currentColor" />
                </div>
                <div>
                  <p className="text-xs font-black text-slate-400 uppercase tracking-wider">Service confidence</p>
                  <p className="text-sm font-black text-slate-900">Verified pros, tracked jobs</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
